import { useState, useEffect, useRef } from "react";
import { ContratTemplate } from "../../model/Model";
import { getTemplateById } from "../../service/templates/templates.service";
import QuillWrapper from './QuillWrapper';

interface AjouterTemplatePopupProps {
    ajouterModifier: (template: ContratTemplate) => Promise<void>;
    onClose: () => void;
    templateId?: number;
}

export function AjouterTemplatePopup({ ajouterModifier, onClose, templateId }: AjouterTemplatePopupProps) {
    const [template, setTemplate] = useState<ContratTemplate>({
        nom: "",
        description: "",
        type: "VIDE",
        contenu: ""
    });
    
    const quillRef = useRef<any>(null);

    useEffect(() => {
        async function fetchTemplate() {
            if (!templateId) return;
            const data = await getTemplateById(templateId);
            if (data) {
                setTemplate(data);
            }
        }
        fetchTemplate();
    }, [templateId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await ajouterModifier(template);
        // Reset form
        setTemplate({
            nom: "",
            description: "",
            type: "VIDE",
            contenu: ""
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setTemplate(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleQuillChange = (content: string) => {
        setTemplate(prev => ({
            ...prev,
            contenu: content
        }));
    };

    const insertVariable = (variable: string) => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const cursorPosition = quill.getSelection()?.index || 0;
            quill.insertText(cursorPosition, variable);
            quill.setSelection(cursorPosition + variable.length, 0);
        }
    };

    const variables = [
        { label: "Nom locataire", value: "{{LOCATAIRE_NOM}}" },
        { label: "Prénom locataire", value: "{{LOCATAIRE_PRENOM}}" },
        { label: "Adresse bien", value: "{{BIEN_ADRESSE}}" },
        { label: "Montant loyer", value: "{{MONTANT_LOYER}}" },
        { label: "Montant charges", value: "{{MONTANT_CHARGES}}" },
        { label: "Date début", value: "{{DATE_DEBUT}}" },
        { label: "Date fin", value: "{{DATE_FIN}}" },
        { label: "Dépôt de garantie", value: "{{DEPOT_GARANTIE}}" },
    ];

    // Configuration de la barre d'outils Quill
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline',
        'list', 'bullet',
        'align'
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {templateId ? "Modifier le template" : "Créer un nouveau template"}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nom du template *
                            </label>
                            <input
                                type="text"
                                name="nom"
                                value={template.nom}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                                placeholder="Ex: Contrat de location meublée"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type de contrat *
                            </label>
                            <select
                                name="type"
                                value={template.type}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            >
                                <option value="VIDE">Location vide</option>
                                <option value="MEUBLE">Location meublée</option>
                                <option value="SAISONNIERE">Location saisonnière</option>
                                <option value="PROFESSIONNEL">Location professionnelle</option>
                                <option value="PARKING">Location parking</option>
                            </select>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <input
                            type="text"
                            name="description"
                            value={template.description}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Description courte du template"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contenu du contrat *
                        </label>
                        
                        {/* Variables dynamiques */}
                        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-xs text-blue-900 font-semibold">Variables dynamiques :</span>
                                {variables.map((v, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => insertVariable(v.value)}
                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                                        title={`Insérer ${v.label}`}
                                    >
                                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-blue-700 mt-2">
                                💡 Cliquez sur une variable pour l'insérer à la position du curseur
                            </p>
                        </div>

                        {/* Éditeur React Quill */}
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                            <QuillWrapper
                                ref={quillRef}
                                value={template.contenu}
                                onChange={handleQuillChange}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Rédigez votre contrat ici... Utilisez la barre d'outils pour formater le texte et les boutons ci-dessus pour insérer les variables."
                                style={{ height: '400px' }}
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            📋 <strong>Formatage :</strong> Utilisez la barre d'outils pour mettre en forme votre texte (gras, souligné, titres, listes, etc.)
                            <br />
                            🔧 <strong>Variables :</strong> Les variables seront automatiquement remplacées par les données réelles lors de la génération du PDF
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-md"
                        >
                            {templateId ? "Mettre à jour" : "Créer le template"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AjouterTemplatePopup;
